import React, { useState, useRef } from 'react';
import ReactMarkdown from 'react-markdown';

function ExpandableSection({ title, children }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="mb-3 sm:mb-4">
      <div
        className="cursor-pointer text-pl_prim_color_1 font-medium flex items-center gap-1 select-none text-sm sm:text-base hover:text-pl_prim_color_3 transition-colors"
        onClick={() => setExpanded((e) => !e)}
      >
        <span className="text-xs sm:text-sm">{expanded ? "▼" : "▶"}</span>{" "}
        <span>{title}</span>
      </div>
      {expanded && (
        <div className="p-2 sm:p-3 bg-pl_bg_light_4 rounded mt-1 text-xs sm:text-sm">
          {children}
        </div>
      )}
    </div>
  );
}

function TagList({ tags }) {
  if (!tags || tags.length === 0) return <span className="text-pl_txt_light_3 italic text-xs sm:text-sm">No tags</span>;
  return (
    <div className="flex flex-wrap gap-1 sm:gap-2 my-1 sm:my-2">
      {tags.map((tag, idx) => (
        <span key={idx} className="bg-pl_border_light_1 rounded-full px-2 sm:px-3 py-0.5 sm:py-1 text-xs font-medium text-pl_txt_light_1">{tag}</span>
      ))}
    </div>
  );
}

function ChatLogDetail({ log }) {
  const finalAnswerRef = useRef(null);
  
  if (!log) return (
    <div className="p-3 sm:p-4 lg:p-8 text-pl_txt_light_2 text-center flex items-center justify-center h-full">
      <div className="text-sm sm:text-base">Select a chat log to view details.</div>
    </div>
  );
  
  const scrollToFinalAnswer = () => {
    finalAnswerRef.current?.scrollIntoView({ 
      behavior: 'smooth',
      block: 'start'
    });
  };

  // Function to render ALL available tags and properties from the log object
  const renderAllLogProperties = () => {
    // Get all properties from the log object
    const allProperties = Object.entries(log);
    
    // Filter out basic properties that are already shown in the header
    const filteredProperties = allProperties.filter(([key]) => 
      !['id', '_id', 'task_id', 'timestamp', 'status'].includes(key)
    );
    
    if (filteredProperties.length === 0) {
      return null;
    }

    return (
      <div className="mb-3 sm:mb-4">
        <div className="space-y-3">
          {filteredProperties.map(([key, value]) => (
            <ExpandableSection key={key} title={key.replace(/_/g, " ")}>
              <div className="bg-pl_bg_light_5 rounded-lg p-3 sm:p-4 border-l-4 border-l-teal-500">
                <div className="font-medium text-pl_txt_light_1 mb-3 capitalize text-lg">
                  {key.replace(/_/g, " ")}
                </div>

                {value === null ? (
                  <div className="text-pl_txt_light_3 italic">
                    No data available
                  </div>
                ) : value === undefined ? (
                  <div className="text-pl_txt_light_3 italic">Undefined</div>
                ) : typeof value === "object" ? (
                  <div className="space-y-2">
                    {Object.entries(value).map(([subKey, subValue]) => (
                      <div
                        key={subKey}
                        className="bg-pl_bg_light_4 rounded p-3 border-l-2 border-l-teal-300"
                      >
                        <div className="font-medium text-pl_txt_light_1 mb-2 capitalize">
                          {subKey.replace(/_/g, " ")}
                        </div>
                        <div className="text-sm text-pl_txt_light_2">
                          {subValue === null ? (
                            <span className="text-pl_txt_light_3 italic">
                              null
                            </span>
                          ) : subValue === undefined ? (
                            <span className="text-pl_txt_light_3 italic">
                              undefined
                            </span>
                          ) : typeof subValue === "object" ? (
                            <pre className="whitespace-pre-wrap break-words bg-pl_bg_light_3 p-2 rounded text-xs overflow-x-auto">
                              {JSON.stringify(subValue, null, 2)}
                            </pre>
                          ) : Array.isArray(subValue) ? (
                            <div className="space-y-2">
                              {subValue.map((item, index) => (
                                <div
                                  key={index}
                                  className="bg-pl_bg_light_3 rounded p-2 text-xs"
                                >
                                  {typeof item === "object" ? (
                                    <pre className="whitespace-pre-wrap break-words">
                                      {JSON.stringify(item, null, 2)}
                                    </pre>
                                  ) : (
                                    <div className="prose prose-sm max-w-none">
                                      <ReactMarkdown>{String(item)}</ReactMarkdown>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="break-words prose prose-sm max-w-none">
                              <ReactMarkdown>{String(subValue)}</ReactMarkdown>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : Array.isArray(value) ? (
                  <div className="space-y-2">
                    {value.map((item, index) => (
                      <div
                        key={index}
                        className="bg-pl_bg_light_4 rounded p-2 text-xs"
                      >
                        {typeof item === "object" ? (
                          <pre className="whitespace-pre-wrap break-words">
                            {JSON.stringify(item, null, 2)}
                          </pre>
                        ) : (
                          <div className="prose prose-sm max-w-none">
                            <ReactMarkdown>{String(item)}</ReactMarkdown>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-sm text-pl_txt_light_2 break-words prose prose-sm max-w-none">
                    <ReactMarkdown>{String(value)}</ReactMarkdown>
                  </div>
                )}
              </div>
            </ExpandableSection>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="p-3 sm:p-4 lg:p-8 flex-1 overflow-y-auto bg-pl_bg_light_3 h-full">
      <h2 className="text-lg sm:text-xl lg:text-2xl font-bold mb-2 text-pl_txt_light_1">Task ID: {log.task_id}</h2>
      <div className="mb-2 text-sm sm:text-base text-pl_txt_light_1">Status: <span className={`font-semibold ${log.status === 'success' ? 'text-green-600' : 'text-red-600'}`}>{log.status}</span></div>
      <div className="mb-2 text-xs sm:text-sm text-pl_txt_light_2">Timestamp: {new Date(log.timestamp?.$date || log.timestamp).toLocaleString()}</div>
      <hr className="my-3 sm:my-4 border-pl_border_light_1" />
      
      {/* Render ALL available properties and tags automatically */}
      {renderAllLogProperties()}
    </div>
  );
}

export default ChatLogDetail; 